<template>
  <div class="container">
    <div class="text-container">
      <TextBase :text="formatter(value, locale, format)" :type="'h3'" :format="format" />
    </div>
    <div class="box gauge">
      <div class="mask">
        <div class="semi-circle"></div>
        <div class="semi-circle--mask" :style="{ transform }"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import TextBase from '@/design-system/atoms/TextBase.vue'
import { FormatOptions, formatter } from './AnalyticsDashboard/formatter'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

type Props = {
  value: number
  min?: number
  max?: number
  color?: string
  format?: FormatOptions
}

const props = withDefaults(defineProps<Props>(), {
  min: 0,
  max: 1,
  color: '#3f46d6',
  format: undefined
})

const value = computed(
  () =>
    (Math.min(Math.max(props.value, props.min), props.max) - props.min) / (props.max - props.min)
)

const transform = computed(() => `rotate(${value.value * 180}deg) translate3d(0, 0, 0)`)

const { locale } = useI18n()
</script>

<style lang="scss" scoped>
$baseFontSize: 16;
$green: v-bind(color);
$yellow: #f1c40f;
$red: #c0392b;

@function rem($val) {
  @return #{calc($val / $baseFontSize)}rem;
}

.container {
  position: relative;
}

.text-container {
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.mask {
  position: relative;
  overflow: hidden;

  display: block;
  width: rem(200);
  height: rem(100);
  left: 50%;
  transform: translateX(-50%);
}
.semi-circle {
  position: relative;

  display: block;
  width: rem(200);
  height: rem(100);

  background: linear-gradient(to right, $red 0%, $yellow 50%, $green 100%);

  border-radius: 50% 50% 50% 50% / 100% 100% 0% 0%;

  &::before {
    content: '';

    position: absolute;
    bottom: 0;
    left: 50%;
    z-index: 2;

    display: block;
    width: rem(140);
    height: rem(70);
    margin-left: rem(-70);

    background: #fff;

    border-radius: 50% 50% 50% 50% / 100% 100% 0% 0%;
  }
}

.semi-circle--mask {
  position: absolute;
  top: 0;
  left: 0;

  width: rem(200);
  height: rem(200);

  background: transparent;

  transform: translate3d(0, 0, 0);
  transform-origin: center center;
  backface-visibility: hidden;
  transition: all 0.3s ease-in-out;

  &::before {
    content: '';

    position: absolute;
    top: 0;
    left: 0%;
    z-index: 2;

    display: block;
    width: rem(202);
    height: rem(102);
    margin: -1px 0 0 -1px;

    background: #f2f2f2;

    border-radius: 50% 50% 50% 50% / 100% 100% 0% 0%;
  }
}

.gauge {
  position: relative;
  .semi-circle {
    background: $green;
  }
}
</style>
